import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pill, AlertCircle, Clock, Info } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { wellnessAPI } from '../lib/api';

export default function SupplementsPage() {
  const [supplements, setSupplements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSupplementRecommendations();
  }, []);

  const loadSupplementRecommendations = async () => {
    try {
      const response = await wellnessAPI.getSupplements();
      setSupplements(response.data.supplements);
    } catch (err) {
      setError('Failed to load supplement recommendations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="spinner mb-4" />
          <p className="text-[#4A2C2A]/60">Generating personalized recommendations...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="supplements-page">
        {/* Back Link */}
        <Link to="/dashboard" className="inline-flex items-center text-[#4A2C2A]/60 hover:text-[#4A2C2A]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Link>

        {/* Header */}
        <div className="soft-card p-8 bg-gradient-to-br from-[#E8A095]/10 to-[#FDFBF7]">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#E8A095]/20 flex items-center justify-center">
              <Pill className="w-7 h-7 text-[#E8A095]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl text-[#4A2C2A]">Supplement Recommendations</h1>
              <p className="text-[#4A2C2A]/60">Personalized supplements based on your health profile</p>
            </div>
          </div>
        </div>

        {error ? (
          <div className="soft-card p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#E8A095]" />
            <p className="text-[#4A2C2A]/70">{error}</p>
            <Button onClick={loadSupplementRecommendations} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : supplements ? (
          <>
            {/* Summary */}
            <div className="soft-card p-6">
              <h2 className="font-heading text-xl text-[#4A2C2A] mb-3">Overview</h2>
              <p className="text-[#4A2C2A]/70 leading-relaxed">{supplements.summary}</p>
            </div>

            {/* Supplement Cards */}
            {supplements.recommendations?.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl text-[#4A2C2A]">Recommended Supplements</h2>
                {supplements.recommendations.map((supp, i) => (
                  <div key={i} className="soft-card p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#E8A095]/20 flex items-center justify-center flex-shrink-0">
                        <Pill className="w-6 h-6 text-[#E8A095]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg text-[#4A2C2A] mb-2">{supp.name}</h3>
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          {supp.dosage && (
                            <div>
                              <p className="text-[#4A2C2A]/50 text-xs mb-1">Dosage</p>
                              <p className="text-[#4A2C2A]/80">{supp.dosage}</p>
                            </div>
                          )}
                          {supp.timing && (
                            <div>
                              <p className="text-[#4A2C2A]/50 text-xs mb-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> When to Take
                              </p>
                              <p className="text-[#4A2C2A]/80">{supp.timing}</p>
                            </div>
                          )}
                        </div>
                        {supp.benefits && (
                          <div className="mt-3 p-3 bg-[#FDFBF7] rounded-xl">
                            <p className="text-xs text-[#4A2C2A]/50 mb-1">Benefits</p>
                            <p className="text-sm text-[#4A2C2A]/70">{supp.benefits}</p>
                          </div>
                        )}
                        {supp.notes && (
                          <p className="mt-2 text-xs text-[#4A2C2A]/50 flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5" /> {supp.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cautions */}
            {supplements.cautions?.length > 0 && (
              <div className="soft-card p-6 border-l-4 border-[#E8C495]">
                <h2 className="font-heading text-lg text-[#4A2C2A] mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[#E8C495]" /> Important Notes
                </h2>
                <ul className="space-y-2">
                  {supplements.cautions.map((caution, i) => (
                    <li key={i} className="text-sm text-[#4A2C2A]/70 flex items-start gap-2">
                      <span className="text-[#E8C495]">•</span>
                      {caution}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer */}
            <div className="p-4 bg-[#FDFBF7] rounded-xl text-center">
              <p className="text-xs text-[#4A2C2A]/50">
                Always consult with a healthcare professional before starting any new supplement regimen.
              </p>
            </div>

            {/* CTA */}
            <div className="soft-card p-6 bg-gradient-to-r from-[#4A2C2A] to-[#2D1A19] text-white">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-xl mb-2">Have Questions?</h3>
                  <p className="text-white/70 text-sm">
                    Chat with your wellness advisor for more personalized guidance.
                  </p>
                </div>
                <Link to="/chat">
                  <Button className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82] rounded-full px-6">
                    Ask Advisor
                  </Button>
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className="soft-card p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#4A2C2A]/30" />
            <p className="text-[#4A2C2A]/70">No supplement recommendations available yet.</p>
            <p className="text-sm text-[#4A2C2A]/50 mt-2">
              Ask your wellness provider to upload relevant documents.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
